import React from 'react';
import { IonButton, IonModal, IonContent, IonHeader, IonToolbar, IonButtons, IonTitle, IonIcon, NavContext } from '@ionic/react';
import store from '../../../store';
import LimeAPI from '../../../classes/LimeAPI';
import isBrowser from '../../../helpers/is-browser';
import { currentUserIsAdmin } from '../../../helpers/roles';
import { loadingController, toastController } from '@ionic/core';
import { withRouter } from 'react-router-dom';
import { storeQualityDocument, storeCurrentQualityDocument, verifyQualityDocumentOnApproval } from '../../../helpers/quality-document';
import { ensureQualityDocumentsLoaded, getNewProjectStatus, setProjectStatus } from '../../../helpers/project';
import SignModal from '../../BankId/SignModal';
import { closeCircle } from 'ionicons/icons';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  buildSignSuccessRedirectUrl,
  SCRIVE_SIGN_BANK_ID_RETURN_URL,
  SCRIVE_SIGN_SUCCESS_URL_PREFIX,
  ScriveSign,
} from '../../../helpers/scrive-sign';
import {
  PendingSign,
  SCRIVE_SIGN_PENDING_KEY,
  clearPendingSign as clearStoredPendingSign,
  clearSigningSession,
  getPendingSign as readPendingSign,
  storeSigningSession,
} from '../../../helpers/signing-session';

interface State {
  isLoading: boolean;
  bankIdMessage: any;
  retries: any;
  qrImageUpdated?: string;
  sameDevice: boolean;
  qrImage: string;
}

class UpdateStatusButton extends React.Component<any, any> {
  static contextType = NavContext;

  abortController = new AbortController();
  finalizeAbortController = new AbortController();
  pollAbortController = new AbortController();
  cancelAbortController = new AbortController();
  _isMounted: boolean;
  pollInFlight = false;
  appActive = true;
  appStateListener: { remove: () => Promise<void> } | null = null;

  constructor(props: any) {
    super(props);

    this.state = {
      isSubmitting: false,
      showModalApprove: false,
      showModalBankId: false,
      fields: [],
      iframeUrl: '',
      signProcessActive: false,
      bankIdSigned: false,
      bankIdMessage: '',
      qrImage: '',
      orderRef: '',
      scriveDocumentId: '',
      qualityDocumentId: null,
      qrImageUpdated: '',
      retries: 0,
    };

    this.updateStatusButtonClick = this.updateStatusButtonClick.bind(this);
    this.updateModalButtonClick = this.updateModalButtonClick.bind(this);

    this.updateStatus = this.updateStatus.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    setTimeout(() => this.resumeSignCheck(), 500);

    App.addListener('appStateChange', (state) => {
      this.appActive = state.isActive;
      if (state.isActive) {
        if (this.getPendingSign() || this.state.signProcessActive) {
          this.resumeSignCheck();
        }
      } else {
        this.pollAbortController.abort();
        this.pollInFlight = false;
        this.pollAbortController = new AbortController();
      }
    }).then((listener) => {
      this.appStateListener = listener;
    }).catch(() => {
      this.appStateListener = null;
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (!this.state.signProcessActive) {
      this.abortController.abort();
    }
    this.pollAbortController.abort();
    this.cancelAbortController.abort();
    this.appStateListener?.remove().catch(() => false);
  }

  getPendingSign(): PendingSign | null {
    return readPendingSign();
  }

  clearPendingSign() {
    clearStoredPendingSign();
    clearSigningSession();
  }

  matchesPendingDocument(pending: PendingSign) {
    const path = this.props.history?.location?.pathname ?? '';
    if (path.includes(`/qualitydocument/${pending.qualityDocumentId}`)) {
      return true;
    }
    const routeDocumentId = this.props.match?.params?.documentId;
    const currentId = store.getState().currentQualityDocument?._id;
    return routeDocumentId && String(pending.qualityDocumentId) === String(routeDocumentId)
      || currentId && String(pending.qualityDocumentId) === String(currentId);
  }

  isApiResponse(data: any) {
    return data && typeof data === 'object' && 'success' in data;
  }

  resumeSignCheck() {
    if (this.pollInFlight) {
      return;
    }

    this.appActive = true;

    const pending = this.getPendingSign();
    let scriveDocumentId = this.state.scriveDocumentId;
    let qualityDocumentId = this.state.qualityDocumentId ?? store.getState().currentQualityDocument?._id;

    if (pending?.scriveDocumentId && pending?.qualityDocumentId) {
      if (!this.matchesPendingDocument(pending)) {
        return;
      }
      scriveDocumentId = pending.scriveDocumentId;
      qualityDocumentId = pending.qualityDocumentId;
    } else if (!scriveDocumentId || !qualityDocumentId) {
      return;
    } else if (!this.state.signProcessActive) {
      return;
    }

    this.setState({
      showModalBankId: true,
      signProcessActive: true,
      scriveDocumentId,
      qualityDocumentId,
      fields: pending?.fields?.length ? pending.fields : this.state.fields,
      bankIdMessage: 'Kontrollerar signeringsstatus...',
      isLoading: false,
      retries: 0,
    });
    this.checkSignStatus(scriveDocumentId, qualityDocumentId);
  }

  async updateQualityDocument(fromSigning = false) {
    const state = store.getState();
    const { currentQualityDocument } = state;
    const { fields } = this.state;
    const fieldsObj: any = {};
    fields.forEach((field: any) => fieldsObj[field] = true);

    const data = {
      qualitydocumentstatus: this.props.updateValue,
      company: currentQualityDocument.company._id,
      _id: currentQualityDocument._id,
      version: Number(currentQualityDocument.version),
      ...fieldsObj,
    };

    data.picture = [ ...currentQualityDocument.picture.map((picture: any) => {
      return 'data:image/' + picture.extension + ';base64,' + picture.data;
    })];

    const signal = fromSigning
      ? this.finalizeAbortController.signal
      : this.abortController.signal;

    return LimeAPI.put(`qualitydocuments/${currentQualityDocument._id}`, data, signal);
  }

  async updateStatusButtonClick() {
    const fields = verifyQualityDocumentOnApproval();
    const { updateValue } = this.props;

    if (fields.length !== 0 && currentUserIsAdmin() && updateValue === 'approved') {
      this.setState({ showModalApprove: true, fields });
      return;
    }

    await this.submitQualityDocument();
    return;
  }

  async submitQualityDocument() {

    if (this.state.isSubmitting) {
      return;
    }

    this.setState({ isSubmitting: true });
    if (this.props.updateValue === 'approved') {
      this.setState((prevState: State) => ({
        ...prevState,
        showModalBankId: true,
        isLoading: true,
        bankIdMessage: 'Initierar signering...',
      }));

      this.signDocument().catch(() => false);

      return;
    }

    this.updateStatus()
      .catch(() => false);
  }

  async presentNativeSigning(signUrl: string, scriveDocumentId: string, qualityDocumentId: number) {
    try {
      const result = await ScriveSign.present({
        url: signUrl,
        bankIdReturnUrl: SCRIVE_SIGN_BANK_ID_RETURN_URL,
        successUrlPrefix: SCRIVE_SIGN_SUCCESS_URL_PREFIX,
      });

      if (!this._isMounted) {
        return;
      }

      if (result.outcome === 'success') {
        this.setState({
          showModalBankId: true,
          bankIdMessage: 'Kontrollerar signeringsstatus...',
          isLoading: true,
        });
        this.checkSignStatus(scriveDocumentId, qualityDocumentId);
        return;
      }

      await this.cancelSignAndClose('Signeringen avbröts.');
    } catch {
      if (!this._isMounted) {
        return;
      }

      this.clearPendingSign();
      const msg = 'Kunde inte öppna signeringsfönstret.';
      this.setState({
        signProcessActive: false,
        bankIdMessage: msg,
        errorMsg: msg,
        isLoading: false,
      });
    }
  }

  async signDocument() {
    if (this.state.signProcessActive) {
      return;
    }
    const state = store.getState();
    const { user, currentQualityDocument, currentProject } = state;

    const adminBaseUrl = Capacitor.isNativePlatform()
      ? 'https://app.bkr.se'
      : window.location.origin;
    const projectId = String(currentProject?.id ?? '');
    const documentId = String(currentQualityDocument._id ?? '');
    const signSuccessRedirectUrl = Capacitor.isNativePlatform()
      ? buildSignSuccessRedirectUrl(projectId, documentId)
      : `${adminBaseUrl}/projects/${projectId}/qualitydocument/${documentId}/`;

    return LimeAPI.post('scrive/signDocument', {
      pin: user.identityNoLong,
      documentId: currentQualityDocument._id,
      signSuccessRedirectUrl,
      projectId: currentProject?.id,
    }, this.abortController.signal)
      .then(data => {
        if (!data.success) {
          const msg = data.message || data.data?.message || 'Något gick fel, försök igen.';
          this.setState({ bankIdMessage: msg, errorMsg: msg, isLoading: false });
          return;
        }

        const signUrl = data.data?.signUrl;
        const scriveDocumentId = data.data?.scriveDocumentId;

        if (!signUrl || !scriveDocumentId) {
          const msg = 'Kunde inte hämta signeringslänk.';
          this.setState({ bankIdMessage: msg, errorMsg: msg, isLoading: false });
          return;
        }

        this.setState({
          signProcessActive: true,
          scriveDocumentId,
          qualityDocumentId: currentQualityDocument._id,
          bankIdMessage: 'Öppnar signering...',
          isLoading: false,
        });

        try {
          localStorage.setItem(SCRIVE_SIGN_PENDING_KEY, JSON.stringify({
            scriveDocumentId,
            qualityDocumentId: currentQualityDocument._id,
            projectId: currentProject?.id,
            fields: this.state.fields,
          }));
        } catch (e) {
          console.warn('Could not store scriveSignPending', e);
        }

        if (Capacitor.isNativePlatform()) {
          this.presentNativeSigning(signUrl, scriveDocumentId, currentQualityDocument._id);
          return;
        }

        storeSigningSession(projectId, documentId);
        window.location.href = signUrl;
      })
      .catch(() => {
        const msg = 'Något gick fel, försök igen.';
        this.setState({
          bankIdMessage: msg,
          errorMsg: msg,
          isLoading: false,
        });
      });
  }

  handleQRImageTimeout = () => {
    this.signDocument().catch(() => false);
  }

  finalizeSignSuccess(data: any) {
    const signaturename = data.data?.signaturename;
    const signdate = data.data?.signdate;
    if (signaturename || signdate) {
      const state = store.getState();
      const qd = state.currentQualityDocument;
      if (qd) {
        storeCurrentQualityDocument({
          qualitydocument: {
            ...qd,
            signaturename: signaturename ?? qd.signaturename,
            signdate: signdate ?? qd.signdate,
          },
        });
      }
    }

    setTimeout(() => {
      if (!this._isMounted) {
        return;
      }

      this.clearPendingSign();
      this.closeModal();

      if (!this.state.hasUpdatedStatus) {
        this.setState({
          hasUpdatedStatus: true,
          signProcessActive: false,
          isLoading: false,
          retries: 0,
        });

        this.updateStatus(true).catch((error) => {
          console.warn('Error in updateStatus:', error);
          if (this._isMounted) {
            this.setState({ hasUpdatedStatus: false });
          }
        });
      }
    }, 1500);
  }

  cancelSignAndClose(message: string) {
    this.pollAbortController.abort();
    this.pollInFlight = false;
    this.pollAbortController = new AbortController();

    const pending = this.getPendingSign();
    const scriveDocumentId = pending?.scriveDocumentId ?? this.state.scriveDocumentId;
    const qualityDocumentId = pending?.qualityDocumentId ?? this.state.qualityDocumentId;

    const resetAndClose = () => {
      this.clearPendingSign();
      this.setState({
        signProcessActive: false,
        isLoading: false,
        retries: 0,
        isSubmitting: false,
        bankIdMessage: message,
      });
      this.closeModal();
    };

    if (!scriveDocumentId || !qualityDocumentId) {
      resetAndClose();
      return Promise.resolve();
    }

    return LimeAPI.post('scrive/signDocument/cancel', {
      scriveDocumentId,
      qualityDocumentId,
    }, this.cancelAbortController.signal).then(data => {
      if (!this._isMounted) {
        return;
      }

      if (this.isApiResponse(data) && data.success && data.data?.status === 'COMPLETE') {
        this.finalizeSignSuccess(data);
        return;
      }

      resetAndClose();
    }).catch(() => {
      if (this._isMounted) {
        resetAndClose();
      }
    });
  }

  scheduleSignStatusRetry(scriveDocumentId: any, qualityDocumentId: any, message: string) {
    if (!this.appActive) {
      this.setState({
        isLoading: false,
        bankIdMessage: message || 'Väntar på signering...',
      });
      return;
    }

    const newRetries = this.state.retries + 1;
    if (newRetries < 30) {
      this.setState({
        isLoading: false,
        bankIdMessage: message || 'Väntar på signering...',
        retries: newRetries,
      });
      setTimeout(() => this.checkSignStatus(scriveDocumentId, qualityDocumentId), 2000);
    } else {
      this.clearPendingSign();
      this.setState({
        signProcessActive: false,
        isLoading: false,
        retries: 0,
        bankIdMessage: message || 'Signeringen tog för lång tid.',
      });
      this.closeModal();
    }
  }

  checkSignStatus(scriveDocumentId: any, qualityDocumentId: any) {
    this.pollInFlight = true;
    LimeAPI.post('scrive/signDocument/checkSignStatus', {
      scriveDocumentId,
      qualityDocumentId,
    }, this.pollAbortController.signal)
    .then(data => {
      this.pollInFlight = false;
      if (data?.name === 'AbortError') {
        return;
      }
      if (!this._isMounted) {
        return;
      }

      if (!this.isApiResponse(data)) {
        this.scheduleSignStatusRetry(
          scriveDocumentId,
          qualityDocumentId,
          'Väntar på signering...'
        );
        return;
      }

      this.setState({ bankIdMessage: data.data?.message || '' });
      if (!data.success) {
        if (data.data?.retry) {
          this.scheduleSignStatusRetry(
            scriveDocumentId,
            qualityDocumentId,
            data.data?.message || 'Väntar på signering...'
          );
          return;
        }

        this.clearPendingSign();
        this.setState({
          signProcessActive: false,
          isLoading: false,
          retries: 0,
        });
        this.closeModal();
        return;
      }

      if (data.data?.retry) {
        this.scheduleSignStatusRetry(
          scriveDocumentId,
          qualityDocumentId,
          data.data?.message || 'Väntar på signering...'
        );
        return;
      }

      if (data.data?.status === 'COMPLETE') {
        this.finalizeSignSuccess(data);
      }
    })
    .catch((error) => {
      this.pollInFlight = false;
      if (error?.name === 'AbortError') {
        return;
      }
      if (this._isMounted) {
        console.warn('Error during checkSignStatus:', error);
        this.setState({
          errorMsg: 'Något gick fel, försök igen.',
          isLoading: false,
        });
      }
    });
  }

  async updateStatus(fromSigning = false) {
    const state = store.getState();
    const { currentQualityDocument } = state;

    const controller = await loadingController.create({
      spinner: 'crescent',
      message: 'Uppdaterar status...',
    });

    await controller.present();

    const response = await this.updateQualityDocument(fromSigning);

    loadingController.dismiss().catch();

    if (! response.success) {
      const toast = await toastController.create({
        message: 'Statusen för kvalitetsdokumentet kunde inte uppdateras, försök igen.',
        color: 'dark',
        buttons: ['Ok'],
      });

      await toast.present();

      return;
    }

    storeQualityDocument({
      ...response.data,
    });

    if (this.props.updateValue === 'approved') {
      currentQualityDocument.qualitydocumentstatus = 'approved';
      currentQualityDocument.supplementtoclient = response.data.supplementtoclient;
      currentQualityDocument.copytoauthorizedcompany = response.data.copytoauthorizedcompany;
      currentQualityDocument.signdate = response.data.signdate;
      currentQualityDocument.signaturename = response.data.signaturename;

      const data = {
        qualitydocument: currentQualityDocument,
      };
      storeCurrentQualityDocument(data);
    }

    await ensureQualityDocumentsLoaded(
      fromSigning ? this.finalizeAbortController.signal : this.abortController.signal
    );

    const projectStatuses = getNewProjectStatus();

    if (projectStatuses) {
      await setProjectStatus(projectStatuses['projectstatus'], projectStatuses['status_tiler']);
    }

    if (this._isMounted) {
      this.setState({ isSubmitting: false });
    }

    if (this.props.updateValue === 'approved') {
      return this.props.openActionSheet();
    } else {
      const toastSuccess = await toastController.create({
        message: 'Statusen för kvalitetsdokumentet har nu uppdaterats.',
        duration: 3000,
        color: 'success',
      });

      await toastSuccess.present();

      return this.context.navigate(`/projects/${state.currentProject.id}`, 'back');
    }
  }

  closeModal() {
    this.setState({
      showModalApprove: false,
      showModalBankId: false,
      isSubmitting: false,
    });
  }

  async updateModalButtonClick(submit: boolean) {
    this.setState({ showModalApprove: false });

    if (submit) {
      await this.submitQualityDocument();
      return;
    }
  }

  render() {
    const fieldLabels: any = {
      'copytoauthorizedcompany': 'Kopia sparad hos behörigt företag',
      'supplementtoclient': 'Kvalitetsdokument lämnad till beställare',
    };

    const {
      showModalApprove,
      showModalBankId,
      fields,
      bankIdMessage,
      isLoading,
      iframeUrl,
      qrImageUpdated,
    } = this.state;

    const modal =
    <IonModal
      isOpen={showModalApprove}
      onDidDismiss={() => this.setState({ showModalApprove: false })}
      cssClass="modal-approve"
      key="modalApprove">
      <IonHeader color="primary">
        <IonToolbar color="primary">
          <IonButtons slot="end">
            <IonButton onClick={() => this.closeModal()}>
              <IonIcon icon={closeCircle} />
            </IonButton>
          </IonButtons>
          <IonTitle>Slutgodkänn kvalitetsdokument</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div>
          <h3>Genom att slutgodkänna blir dessa fält godkända:</h3>
          {fields.map((field: any) => <li key={field}>{fieldLabels[field]}</li>)}
        </div>
      </IonContent>
      <div className="d-flex">
        <IonButton color="danger" onClick={() => this.updateModalButtonClick(false)}>Avbryt</IonButton>
        <IonButton color="success"onClick={() => this.updateModalButtonClick(true)}>Slutgodkänn</IonButton>
      </div>
    </IonModal>;

    const markup = [];

    markup.push(
      <IonButton
      key="buttonUpdateStatus"
      color={this.props.color}
      className="ion-margin-bottom ion-text-wrap"
      expand="block"
      onClick={this.updateStatusButtonClick}
      >
        {this.props.label}
    </IonButton>);

    markup.push(modal);

    markup.push(
      <SignModal
        key="modalBankId"
        closeModal={this.closeModal}
        showModal={showModalBankId}
        message={bankIdMessage}
        qrImage={qrImageUpdated}
        isLoading={isLoading}
        onQRImageTimeout={this.handleQRImageTimeout}
        iframeUrl={iframeUrl} />
    );

    return(
      [markup]
    );
  }
}
export default withRouter(UpdateStatusButton);
